import '../styles/home.css'
import React from 'react'

import LandingGlobalHeader from '../components/landing/LandingGlobalHeader'
import LandingFooter from '../components/landing/LandingFooter'
import useLandingAuth from '../components/landing/useLandingAuth'

const FAQ_SECTIONS = [
  {
    id: 'general-overview',
    number: '01',
    title: 'General Overview',
    items: [
      {
        question: 'What is Latin Macro Watch?',
        answer: 'Latin Macro Watch (LMW) is a comprehensive macroeconomic database developed by the Research Department of the Inter-American Development Bank (IDB). It consolidates official statistics produced by central banks, ministries of finance, and national statistical offices across all 26 IDB borrowing countries in Latin America and the Caribbean into a single standardized platform. The database provides more than 4,500 time series derived from over 180 base indicators, covering more than 35 years of data across six thematic areas.',
      },
      {
        question: 'Who produces and maintains Latin Macro Watch?',
        answer: 'LMW is maintained and continuously developed by the IDB Research Department as part of its mandate to support evidence-based policy analysis in Latin America and the Caribbean. The database is refreshed on a monthly basis by a dedicated team that monitors official releases from primary national sources across all 26 covered countries. The database is freely accessible through the LMW web platform at lmwfislac.com.',
      },
      {
        question: 'Who is the intended audience for Latin Macro Watch?',
        answer: 'LMW serves policymakers, researchers, international organizations, journalists, and analysts who require timely and granular macroeconomic data for Latin America and the Caribbean.',
      },
      {
        question: 'How does Latin Macro Watch differ from other global macroeconomic databases?',
        answer: 'LMW distinguishes itself in three main ways: exclusivity, cross-country consistency, and timeliness. More than one third of its series are not available in other global databases. Every indicator follows a unified definition and standardized units, enabling direct comparison across countries. The database is also refreshed monthly, unlike many sources that update only annually or quarterly.',
      },
    ],
  },
  {
    id: 'data-coverage-and-scope',
    number: '02',
    title: 'Data Coverage and Scope',
    items: [
      {
        question: 'Which countries does Latin Macro Watch cover?',
        answer: 'LMW covers all 26 IDB borrowing member countries. South America includes Argentina, Bolivia, Brazil, Chile, Colombia, Ecuador, Guyana, Paraguay, Peru, Suriname, Uruguay, and Venezuela. Central America and Mexico include Belize, Costa Rica, El Salvador, Guatemala, Honduras, Mexico, Nicaragua, and Panama. The Caribbean includes The Bahamas, Barbados, Dominican Republic, Haiti, Jamaica, and Trinidad and Tobago.',
      },
      {
        question: 'What thematic areas does Latin Macro Watch cover?',
        answer: 'LMW organizes indicators into six thematic areas: Economic Activity and Employment; Prices and Wages; Fiscal Accounts and Public Debt; External Accounts; Money and Banking; and Financial Markets.',
      },
      {
        question: 'How far back does the historical data go?',
        answer: 'The database covers the period from 1990 onward wherever official data are available. If a source publishes data predating 1990, the series is truncated at January 1990. Where publication began after 1990, the series starts at the first available observation. Annual series generally provide the longest history, while quarterly and monthly series may begin later.',
      },
      {
        question: 'At what frequencies is data available in Latin Macro Watch?',
        answer: 'Every series in LMW is stored at monthly, quarterly, or annual frequency depending on the underlying national source. Monthly data typically include prices, monetary aggregates, exchange rates, interest rates, and high-frequency activity indicators. Quarterly data generally cover national accounts, balance of payments, external debt, and labor market aggregates. Annual data cover the broadest set of indicators and usually provide the greatest historical depth.',
      },
    ],
  },
  {
    id: 'data-structure-and-identifiers',
    number: '03',
    title: 'Data Structure and Identifiers',
    items: [
      {
        question: 'How is the Latin Macro Watch dataset structured?',
        answer: 'The dataset is distributed in long, tidy format, where each row corresponds to a single observation for one indicator, one country, one frequency, and one date. Key columns include lmwuuid, Country, Frequency, Date, Value, Indicator, Unit, Transformation, Area, Topic, Definition, Source, Notes, and Updated.',
      },
      {
        question: 'What is the lmwuuid and what information does it encode?',
        answer: 'The lmwuuid is a unique 12-digit numeric identifier assigned to every time series in LMW. It encodes the thematic area, topic, indicator, period code, unit, and transformation in fixed positions. It also serves as the stable key for programmatic access via the LMW API.',
      },
      {
        question: 'What is the primary key of the LMW dataset?',
        answer: 'The primary key is the combination lmwuuid + Country + Frequency + Date. Together, those four fields uniquely identify a single observation.',
      },
      {
        question: 'What is the difference between a unit and a transformation in Latin Macro Watch?',
        answer: 'A unit refers to the representational form in which an indicator is expressed, such as millions of domestic currency, millions of USD, percentage points, percentage of GDP, or constant prices. A transformation is a temporal or analytical operation applied to a series, such as year-on-year percentage change, quarter-on-quarter change, or moving averages. In the lmwuuid, positions 9–10 encode the unit and positions 11–12 encode the transformation.',
      },
      {
        question: 'How are dates represented in the LMW dataset?',
        answer: 'All dates use ISO 8601 format (YYYY-MM-DD) and always correspond to the last calendar day of the reference period. Monthly series use the last day of the month, quarterly series use the last day of the quarter, and annual series use December 31 of the reference year.',
      },
    ],
  },
  {
    id: 'data-sources-and-methodology',
    number: '04',
    title: 'Data Sources and Methodology',
    items: [
      {
        question: 'Where does Latin Macro Watch source its data?',
        answer: 'LMW draws exclusively from official national statistical publications, including central banks, ministries of finance, national statistical offices, and equivalent authorities. Annual population figures used for per-capita indicators come from the World Bank World Development Indicators. Some indicators are internally derived from combinations of primary data and are marked accordingly in the Source field.',
      },
      {
        question: 'Does LMW apply any estimation or interpolation to fill missing observations?',
        answer: 'No. LMW does not automatically impute missing values. If a primary source does not publish data for a specific period, the corresponding value remains empty. Where a source or the methodology involves specific estimation procedures, these are documented explicitly in the Notes field.',
      },
      {
        question: 'How does LMW aggregate series across time periods?',
        answer: 'Aggregation depends on the type of variable. Stock and index variables can be aggregated as average of period or end of period, while flow variables are summed over the reference period. LMW computes lower-frequency series only when all required sub-period observations are available.',
      },
    ],
  },
  {
    id: 'data-access-and-usage',
    number: '05',
    title: 'Data Access and Usage',
    items: [
      {
        question: 'How can I access Latin Macro Watch data?',
        answer: 'LMW data is freely accessible through three channels: the web platform at lmwfislac.com, programmatic API access through the IDB Open Data platform, and full bulk download via the IDB Open Data portal and the LMW Data Center.',
      },
      {
        question: 'Is registration required to use Latin Macro Watch?',
        answer: 'No. Registration is not required for access to the data through the web platform, API, or bulk download. Registered users can access additional personalization features such as dashboards and subscription alerts.',
      },
      {
        question: 'Under what terms can LMW data be used or cited?',
        answer: 'The dataset is published under the Creative Commons Attribution 4.0 International licence. Users may share and adapt the data provided they give appropriate credit. The recommended citation is: Inter-American Development Bank, 2025, Latin Macro Watch Dataset, IDB Open Data. https://doi.org/10.60966/2n3c3oib',
      },
    ],
  },
  {
    id: 'updates-and-revisions',
    number: '06',
    title: 'Updates and Revisions',
    items: [
      {
        question: 'How frequently is Latin Macro Watch updated?',
        answer: 'LMW operates on a monthly rolling update cycle. During the last two weeks of each month, the team reviews and incorporates new data releases from all primary national sources. Updated series are generally made available from the first week of the following month.',
      },
      {
        question: 'How does LMW handle revisions to previously published data?',
        answer: 'When a national source revises previously published data, LMW incorporates the revised figures at the next monthly update. The database stores only the most current version of each series as published by the national authority. If a source permanently discontinues an indicator, LMW retains the series frozen at the last available observation and documents that in the Notes field.',
      },
      {
        question: 'What does the Updated field in the dataset indicate?',
        answer: 'The Updated field records the date of the most recent LMW refresh cycle at the dataset level. It does not mean that a specific series necessarily received new observations in that cycle.',
      },
    ],
  },
  {
    id: 'limitations-and-interpretation',
    number: '07',
    title: 'Limitations and Interpretation',
    items: [
      {
        question: 'Are LMW series directly comparable across countries?',
        answer: 'With caveats. LMW applies a unified definitional framework, but the underlying data still come from 26 distinct national statistical systems with different methodologies, institutional coverage, and base-year conventions. Cross-country comparisons should therefore be interpreted carefully, especially for fiscal, monetary, and labor market indicators.',
      },
      {
        question: 'What should I know when using fiscal data in Latin Macro Watch?',
        answer: 'LMW reports fiscal data at three institutional coverage levels: Non-Financial Public Sector, General Government, and Central Government. Users should choose the coverage level that fits their analytical purpose and be aware that the precise institutional perimeter can vary by country. Country-specific deviations are documented in the Notes field where relevant.',
      },
    ],
  },
]

function FAQScreen() {
  const { loadingUser, userInfo, openModal, logoutHandler, authModals } = useLandingAuth()

  const scrollToSection = (id) => {
    const node = document.getElementById(id)
    if (!node) return
    const y = node.getBoundingClientRect().top + window.scrollY - 110
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  return (
    <div className='landing-page-shell landing-secondary-page'>
      <LandingGlobalHeader
        loadingUser={loadingUser}
        userInfo={userInfo}
        onOpenModal={openModal}
        onLogout={logoutHandler}
      />

      <main>
        <section className='landing-page-hero landing-page-hero-about landing-faq-hero'>
          <div className='container landing-page-hero-inner narrow'>
            <span className='landing-page-eyebrow'>Research Department</span>
            <h1>Latin Macro Watch Frequently Asked Questions</h1>
            <div className='landing-page-hero-bar'></div>
            <div className='landing-faq-meta'>
              <span>Inter-American Development Bank</span>
              <span>lmw@iadb.org</span>
              <span>lmwfislac.com</span>
            </div>
            <p>
              This document provides answers to the most frequently asked questions about Latin Macro Watch (LMW), a comprehensive macroeconomic database covering all 26 IDB borrowing countries in Latin America and the Caribbean. All answers are based strictly on the Latin Macro Watch Technical Documentation: Data Structure and Methodology published by the IDB Research Department.
            </p>
          </div>
        </section>

        <section className='landing-faq-section'>
          <div className='container landing-faq-layout'>
            <aside className='landing-faq-toc'>
              <div className='landing-faq-toc-card'>
                <span className='landing-section-kicker'>Contents</span>
                <div className='landing-faq-toc-links'>
                  {FAQ_SECTIONS.map(section => (
                    <button key={section.id} type='button' onClick={() => scrollToSection(section.id)}>
                      <span>{section.number}</span>
                      <strong>{section.title}</strong>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <div className='landing-faq-content'>
              {FAQ_SECTIONS.map(section => (
                <section className='landing-faq-block' id={section.id} key={section.id}>
                  <div className='landing-faq-block-header'>
                    <span>{section.number}</span>
                    <h2>{section.title}</h2>
                  </div>

                  <div className='landing-faq-qa-list'>
                    {section.items.map((item, index) => (
                      <article className='landing-faq-qa-card' key={item.question}>
                        <div className='landing-faq-question'>
                          <span>{index + 1}</span>
                          <h3>{item.question}</h3>
                        </div>
                        <p>{item.answer}</p>
                      </article>
                    ))}
                  </div>
                </section>
              ))}

              <section className='landing-faq-note'>
                <p>
                  All content in this FAQ is based exclusively on the <strong>Latin Macro Watch Technical Documentation: Data Structure and Methodology</strong>, published by the IDB Research Department. For further information, contact <a href='mailto:lmw@iadb.org'>lmw@iadb.org</a> or visit <a href='https://lmwfislac.com' target='_blank' rel='noreferrer'>lmwfislac.com</a>.
                </p>
              </section>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
      {authModals}
    </div>
  )
}

export default FAQScreen
